import type { CSSProperties, ComponentType } from 'react';
import { GhostWeightLabel, ProximityScope } from '@wapharma/design-system';
import styles from './FilterPillGroup.module.css';

const PILL_ICON_SIZE = 14;
const PILL_ICON_STROKE = 1.8;

export type FilterPillPalette = {
  accent: string;
  bg: string;
  border: string;
};

export type FilterPillTokenPalette = {
  useToken: true;
  cssColor: string;
  cssBg: string;
  cssBorder: string;
};

export type FilterPillDef = {
  value: string;
  label: string;
  icon: ComponentType<{
    size?: number | string;
    stroke?: number;
    className?: string;
    'aria-hidden'?: boolean;
  }>;
} & (FilterPillPalette | FilterPillTokenPalette);

export type FilterPillOption = FilterPillDef & {
  count?: number;
};

function isTokenPalette(opt: FilterPillDef): opt is FilterPillDef & FilterPillTokenPalette {
  return 'useToken' in opt && opt.useToken === true;
}

/** Palette CSS vars only — hover/active handled in CSS (inline colors blocked hover). */
function pillPaletteStyle(opt: FilterPillDef): CSSProperties {
  if (isTokenPalette(opt)) {
    return {
      ['--pill-accent' as string]: `color-mix(in srgb, ${opt.cssColor} 78%, var(--wp-text-primary))`,
      ['--pill-bg' as string]: `color-mix(in srgb, ${opt.cssBg} 10%, var(--wp-card-white))`,
      ['--pill-border' as string]: `color-mix(in srgb, ${opt.cssBorder} 25%, transparent)`,
    };
  }
  return {
    ['--pill-accent' as string]: opt.accent,
    ['--pill-bg' as string]: opt.bg,
    ['--pill-border' as string]: opt.border,
  };
}

export type FilterPillGroupProps = {
  options: readonly FilterPillOption[];
  value: string;
  onChange: (value: string) => void;
  'aria-label': string;
  className?: string;
  showCount?: boolean;
  /** Horizontal scroll row (filter dock on narrow viewports). Grid = report-issue type picker. */
  layout?: 'wrap' | 'scroll' | 'grid';
};

export function FilterPillGroup({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
  showCount = true,
  layout = 'wrap',
}: FilterPillGroupProps) {
  return (
    <ProximityScope
      axis={layout === 'grid' ? 'xy' : 'x'}
      className={
        className
          ? `${styles.group} ${layout === 'scroll' ? styles.groupScroll : ''} ${layout === 'grid' ? styles.groupGrid : ''} ${className}`
          : `${styles.group} ${layout === 'scroll' ? styles.groupScroll : ''} ${layout === 'grid' ? styles.groupGrid : ''}`
      }
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value || '_all'}
            type="button"
            role="radio"
            aria-checked={active}
            data-wp-proximity-item=""
            className={active ? `${styles.pill} ${styles.pillActive}` : styles.pill}
            style={pillPaletteStyle(opt)}
            onClick={() => onChange(opt.value)}
          >
            <Icon size={PILL_ICON_SIZE} stroke={PILL_ICON_STROKE} aria-hidden />
            <GhostWeightLabel active={active}>{opt.label}</GhostWeightLabel>
            {showCount && opt.count !== undefined ? (
              <span className={styles.count}>({opt.count})</span>
            ) : null}
          </button>
        );
      })}
    </ProximityScope>
  );
}
